import { resolveStaticSvg } from '../../utils/knockoutRecord.js';
import { CARTO_TILE_URL, CARTO_TILE_OPTIONS } from '../../constants/mapConstants.js';

export function createCarteMapFeature({ loadFont, generateSVG, escapeHtml, getDashboardItems, getArPhotos, getMapPhotos }) {
  const RESET_VIEW_MAX_ZOOM = 15;
  const SPIDER_RADIUS  = 80;  // rayon estimé (px) du dépli de cluster
  const EDGE_MARGIN    = 90;  // marge minimale (px) bords gauche/droite du viewport
  const EDGE_MARGIN_V  = 175; // marge minimale (px) bords haut/bas du viewport (>= bouton recentrer ~162px)
  const CLUSTER_RADIUS = 40;  // rayon (px) de regroupement des marqueurs (markercluster)

  function getPopupAutoPanOpts() {
    return {
      autoPanPaddingTopLeft: L.point(10, 10),
      autoPanPaddingBottomRight: L.point(80, 45),
    };
  }

  function getTopRightControlsWidth() {
    // le bouton "Tout voir"/"Retour" est plus large que le contrôle de zoom :
    // on mesure le conteneur réel plutôt que de deviner une largeur fixe
    const el = carteMap?.getContainer()?.querySelector('.leaflet-top.leaflet-right');
    const width = el?.getBoundingClientRect().width;
    return width ? Math.ceil(width) + 15 : EDGE_MARGIN;
  }

  function getFittedCenterZoom(bounds, opts, forcedZoom) {
    // Reproduit le calcul interne de L.Map#fitBounds (privé), via l'API publique :
    // on recalcule toujours le centre pour le zoom visé (fitBounds natif ne
    // recalcule le centre que pour SON zoom calculé — si on zoome ensuite d'un
    // cran de plus autour de ce même centre sans redemander ce calcul, la marge
    // de réserve n'est plus respectée sur les écrans étroits en portrait).
    const paddingTL = L.point(opts.paddingTopLeft || opts.padding || [0, 0]);
    const paddingBR = L.point(opts.paddingBottomRight || opts.padding || [0, 0]);
    let zoom = forcedZoom;
    if (zoom == null) {
      zoom = carteMap.getBoundsZoom(bounds, false, paddingTL.add(paddingBR));
      if (typeof opts.maxZoom === 'number') zoom = Math.min(opts.maxZoom, zoom);
    }
    const paddingOffset = paddingBR.subtract(paddingTL).divideBy(2);
    const swPoint = carteMap.project(bounds.getSouthWest(), zoom);
    const nePoint = carteMap.project(bounds.getNorthEast(), zoom);
    const center = carteMap.unproject(swPoint.add(nePoint).divideBy(2).add(paddingOffset), zoom);
    return { center, zoom };
  }

  function fitBoundsWithReserve(bounds, opts = {}) {
    // On ne passe pas par L.Map#fitBounds() : ce dernier délègue en interne à un
    // raccourci de "pan animé" qui, quand le zoom cible tombe pile sur le zoom
    // courant, refuse un déplacement trop grand pour être animé sans jamais
    // retomber sur un reset classique — la carte ne bouge alors pas du tout.
    // setView({reset:true}) est le chemin fiable dans tous les cas.
    const fitted = getFittedCenterZoom(bounds, opts);
    carteMap.setView(fitted.center, fitted.zoom, { reset: true });

    // Zone de réserve : un cran de zoom en plus, avec un centre recalculé pour
    // ce zoom précis (cf. commentaire de getFittedCenterZoom), sans dépasser
    // RESET_VIEW_MAX_ZOOM.
    const reserveZoom = Math.min(fitted.zoom + 1, RESET_VIEW_MAX_ZOOM);
    if (reserveZoom > fitted.zoom) {
      const reserved = getFittedCenterZoom(bounds, opts, reserveZoom);
      carteMap.setView(reserved.center, reserveZoom, { reset: true });
    }
  }

  function getFitBoundsPadding() {
    // padding symétrique gauche/droite pour centrer les clusters
    // EDGE_MARGIN/EDGE_MARGIN_V servent de plancher pour éviter les bords et les éléments UI
    const rightMargin = Math.max(EDGE_MARGIN, getTopRightControlsWidth());
    return {
      paddingTopLeft:     [EDGE_MARGIN, EDGE_MARGIN_V],
      paddingBottomRight: [rightMargin, EDGE_MARGIN_V],
    };
  }

  let carteMap = null;
  let clusterLayer = null;
  let mapLegendControl = null;
  let resetViewControl = null;
  let initialView = null;
  let isProgrammaticMapMove = false;
  const viewHistory = []; // pile des vues précédentes (navigation cluster par cluster)
  let programmaticMapMoveTimer = null;
  let hasAutoFit = false;
  const carteMarkers = new Map();
  const photoMarkers = [];
  const mapPhotoMarkers = [];
  // Photo -> marqueur (+ rang dans son carrousel), pour le lien "Voir sur la
  // carte" de la page Escales (/carte?photo=ar-12, cf. focusPhoto).
  const photoTargets = new Map();
  let pendingPopupStart = null; // { marker, index } : 1re page du prochain popup
  // Lu dès le chargement : le démarrage de l'appli (appBootstrap.js) réécrit
  // ensuite l'URL sans ses paramètres, parfois avant que la carte soit prête.
  let pendingPhotoKey = window.location.pathname === '/carte'
    ? new URLSearchParams(window.location.search).get('photo')
    : null;
  let markerGen = 0;

  const snap = (value) => Math.round(value * 2000) / 2000;

  function buildClusterIconHtml() {
    return (
      '<div class="carte-cluster-icon carte-cluster-icon--minimal">'
      + '<div class="carte-cluster-icon-shapes" aria-hidden="true">'
      + '<span class="carte-cluster-shape carte-cluster-shape--circle" title="Punchlines">●</span>'
      + '<span class="carte-cluster-shape carte-cluster-shape--square" title="Photos AR">■</span>'
      + '<span class="carte-cluster-shape carte-cluster-shape--star" title="Photos carte">★</span>'
      + '</div>'
      + '</div>'
    );
  }

  function createClusterLayerIfAvailable() {
    if (!carteMap || clusterLayer || typeof L.markerClusterGroup !== 'function') return;

    let currentSpiderfiedCluster = null;
    let isPopupOpen = false;

    clusterLayer = L.markerClusterGroup({
      maxClusterRadius: CLUSTER_RADIUS,
      disableClusteringAtZoom: 16,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: false,
      showCoverageOnHover: false,
      chunkedLoading: true,
      iconCreateFunction: () => {
        const size = 35;
        return L.divIcon({
          html: buildClusterIconHtml(),
          className: 'carte-cluster-wrapper',
          iconSize: [size, size],
        });
      },
    });

    clusterLayer.on('clusterclick', (e) => {
      if (!carteMap) { updateResetViewControlState(); return; }

      const bounds = e.layer.getBounds();

      // point-cluster (tous les points au même endroit) : spiderfy direct sans changer le zoom
      if (!bounds.isValid()
        || (bounds.getNorth() - bounds.getSouth() < 0.01
            && bounds.getEast() - bounds.getWest() < 0.01)) {
        if (typeof e.layer.spiderfy === 'function') e.layer.spiderfy();
        updateResetViewControlState();
        return;
      }

      // cluster étendu : fitBounds avec notre padding
      viewHistory.push({ center: carteMap.getCenter(), zoom: carteMap.getZoom() });
      updateResetViewControlState();
      beginProgrammaticViewChange();
      carteMap.fitBounds(bounds, {
        ...getFitBoundsPadding(),
        maxZoom: RESET_VIEW_MAX_ZOOM,
        animate: true,
      });
    });

    clusterLayer.on('spiderfied', (e) => {
      currentSpiderfiedCluster = e.cluster;
      if (!carteMap) return;
      const clusterPt = carteMap.latLngToContainerPoint(e.cluster.getLatLng());
      const mapSize   = carteMap.getSize();
      const fitOpts   = getFitBoundsPadding();
      const padTL     = fitOpts.paddingTopLeft;
      const padBR     = fitOpts.paddingBottomRight;

      const safeL = Math.max(padTL[0], EDGE_MARGIN);
      const safeT = Math.max(padTL[1], EDGE_MARGIN_V);
      const safeR = Math.max(padBR[0], EDGE_MARGIN);
      const safeB = Math.max(padBR[1], EDGE_MARGIN_V);

      const overshootRight  = (clusterPt.x + SPIDER_RADIUS) - (mapSize.x - safeR);
      const overshootLeft   = safeL - (clusterPt.x - SPIDER_RADIUS);
      const overshootBottom = (clusterPt.y + SPIDER_RADIUS) - (mapSize.y - safeB);
      const overshootTop    = safeT - (clusterPt.y - SPIDER_RADIUS);

      const dx = overshootRight > 0 ? overshootRight : overshootLeft > 0 ? -overshootLeft : 0;
      const dy = overshootBottom > 0 ? overshootBottom : overshootTop > 0 ? -overshootTop : 0;

      if (dx !== 0 || dy !== 0) {
        beginProgrammaticViewChange();
        carteMap.panBy([dx, dy], { animate: true, duration: 0.3 });
      }
    });

    clusterLayer.on('unspiderfied', () => {
      currentSpiderfiedCluster = null;
    });

    carteMap.addLayer(clusterLayer);

    // Tracker fiable de l'état popup (carteMap._popup peut rester stale après fermeture via X)
    carteMap.on('popupopen',  () => { isPopupOpen = true; });
    carteMap.on('popupclose', () => { isPopupOpen = false; });

    // preclick se déclenche avant click dans Leaflet, et AVANT le handler popupclose
    // du popup. À ce moment isPopupOpen est encore true si un popup est affiché.
    // On retire le listener click→_unspiderfyWrapper pour que ce même clic ne ferme
    // pas la constellation, puis on le remet après le cycle d'événements.
    carteMap.on('preclick', () => {
      if (!currentSpiderfiedCluster || !isPopupOpen) return;
      if (!clusterLayer._unspiderfyWrapper) return; // propriété privée markercluster 1.5.x
      carteMap.off('click', clusterLayer._unspiderfyWrapper, clusterLayer);
      setTimeout(() => {
        if (clusterLayer && carteMap) {
          carteMap.on('click', clusterLayer._unspiderfyWrapper, clusterLayer);
        }
      }, 0);
    });

    // Filet : si sur certains navigateurs unspiderfy est appelé avant popupclose,
    // on le bloque via isPopupOpen qui sera encore true à ce moment-là.
    if (typeof clusterLayer.unspiderfy === 'function') {
      const origUnspiderfy = clusterLayer.unspiderfy;
      clusterLayer.unspiderfy = function (...args) {
        if (isPopupOpen) {
          return clusterLayer;
        }
        return origUnspiderfy.apply(clusterLayer, args);
      };
    }
  }

  function createMapLegendControl() {
    if (!carteMap || mapLegendControl) return;

    mapLegendControl = L.control({ position: 'bottomleft' });
    mapLegendControl.onAdd = () => {
      const container = L.DomUtil.create('div', 'carte-map-legend-container leaflet-control');

      const panel = L.DomUtil.create('div', 'carte-map-legend', container);
      panel.innerHTML = ''
        + '<div class="carte-map-legend-title">Légende</div>'
        + '<div class="carte-map-legend-row"><span class="carte-map-legend-shape">●</span><span>Punchlines</span></div>'
        + '<div class="carte-map-legend-row"><span class="carte-map-legend-shape">■</span><span>Photos AR</span></div>'
        + '<div class="carte-map-legend-row"><span class="carte-map-legend-shape">★</span><span>Photos carte</span></div>';

      const toggleBtn = L.DomUtil.create('button', 'carte-map-legend-toggle', container);
      toggleBtn.type = 'button';
      toggleBtn.textContent = 'i';
      toggleBtn.setAttribute('aria-label', 'Afficher la légende');
      toggleBtn.setAttribute('aria-expanded', 'false');

      L.DomEvent.on(toggleBtn, 'click', () => {
        const isOpen = panel.classList.toggle('is-open');
        toggleBtn.classList.toggle('is-active', isOpen);
        toggleBtn.setAttribute('aria-expanded', String(isOpen));
        toggleBtn.setAttribute('aria-label', isOpen ? 'Masquer la légende' : 'Afficher la légende');
      });

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      return container;
    };

    mapLegendControl.addTo(carteMap);
  }

  function isAtInitialView() {
    if (!carteMap || !initialView) return true;
    const center = carteMap.getCenter();
    const sameZoom = carteMap.getZoom() === initialView.zoom;
    const centerDistance = center.distanceTo(initialView.center);
    return sameZoom && centerDistance < 20;
  }

  function buildResetViewFromLatLngs(latlngs) {
    if (!carteMap || latlngs.length === 0) return null;
    const bounds = L.latLngBounds(latlngs);
    if (!bounds.isValid()) return null;

    const fitOpts = getFitBoundsPadding();
    const paddingPoint = L.point(
      fitOpts.paddingTopLeft[0] + fitOpts.paddingBottomRight[0],
      fitOpts.paddingTopLeft[1] + fitOpts.paddingBottomRight[1],
    );
    const zoom = Math.min(carteMap.getBoundsZoom(bounds, false, paddingPoint) + 1, RESET_VIEW_MAX_ZOOM);
    return {
      bounds,
      center: bounds.getCenter(),
      zoom,
    };
  }

  function updateResetViewControlState() {
    if (!resetViewControl?.buttonEl) return;
    const atInitialView = isAtInitialView();
    if (atInitialView) { viewHistory.length = 0; }
    const shouldShow = Boolean(initialView) && (viewHistory.length > 0 || !atInitialView);
    resetViewControl.buttonEl.classList.toggle('is-visible', shouldShow);
    resetViewControl.buttonEl.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    const label = viewHistory.length > 0 ? 'Retour' : 'Tout voir';
    resetViewControl.buttonEl.textContent = label;
    resetViewControl.buttonEl.title = label;
    resetViewControl.buttonEl.setAttribute('aria-label', label);
  }

  function beginProgrammaticViewChange() {
    isProgrammaticMapMove = true;
    if (programmaticMapMoveTimer) clearTimeout(programmaticMapMoveTimer);
    programmaticMapMoveTimer = setTimeout(() => {
      isProgrammaticMapMove = false;
      programmaticMapMoveTimer = null;
      updateResetViewControlState();
    }, 600);
  }

  function endProgrammaticViewChange() {
    if (!isProgrammaticMapMove) return;
    isProgrammaticMapMove = false;
    if (programmaticMapMoveTimer) {
      clearTimeout(programmaticMapMoveTimer);
      programmaticMapMoveTimer = null;
    }
  }

  function createResetViewControl() {
    if (!carteMap || resetViewControl) return;

    resetViewControl = L.control({ position: 'topright' });
    resetViewControl.onAdd = () => {
      const container = L.DomUtil.create('div', 'leaflet-control carte-map-reset-control');
      const button = L.DomUtil.create('button', 'carte-map-reset-btn', container);
      button.type = 'button';

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      L.DomEvent.on(button, 'click', (event) => {
        L.DomEvent.stop(event);
        if (!carteMap) return;
        const prev = viewHistory.pop();
        if (prev) {
          beginProgrammaticViewChange();
          carteMap.setView(prev.center, prev.zoom, { animate: true, duration: 0.4 });
          updateResetViewControlState();
          return;
        }
        // plus d'historique : retour à la vue initiale
        // on refait un vrai fitBounds (pas un setView figé) pour que le padding
        // se recalcule sur l'état réel des contrôles au moment du clic
        if (!initialView) return;
        beginProgrammaticViewChange();
        fitBoundsWithReserve(initialView.bounds, {
          ...getFitBoundsPadding(),
          maxZoom: RESET_VIEW_MAX_ZOOM,
          animate: true,
        });
      });

      resetViewControl.buttonEl = button;
      updateResetViewControlState();
      return container;
    };

    resetViewControl.addTo(carteMap);
  }

  // Bascule Mercator (cette page, Leaflet) / Eckert IV (/equalearth, D3) --
  // demande Rudy : deux fonds de carte interchangeables derrière une UI
  // identique. Simples <a> (pas de L.DomEvent.on/click JS) : la navigation
  // change de moteur de rendu entièrement (SPA Leaflet <-> page statique
  // D3), un vrai changement de page est voulu ici, pas une bascule interne.
  function createMapModeControl() {
    if (!carteMap) return;

    const control = L.control({ position: 'topright' });
    control.onAdd = () => {
      const container = L.DomUtil.create('div', 'leaflet-control carte-map-mode-control');
      const group = L.DomUtil.create('div', 'carte-map-mode-controls', container);
      const mercator = L.DomUtil.create('a', 'carte-map-mode-btn is-active', group);
      mercator.href = '/carte';
      mercator.textContent = 'Mercator';
      mercator.setAttribute('aria-current', 'page');
      const eckert = L.DomUtil.create('a', 'carte-map-mode-btn', group);
      eckert.href = '/equalearth';
      eckert.textContent = 'Eckert IV';

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      return container;
    };

    control.addTo(carteMap);
  }

  // Boutons de droite (zoom, Tout voir/Retour, Mercator/Eckert IV) effacés
  // tant qu'un popup ouvert les chevauche : Leaflet les place toujours
  // au-dessus des popups, et sur mobile (popup presque pleine largeur) ils
  // masquaient la croix de fermeture -- sur /equalearth, c'est le popup qui
  // passe devant. Revérifié quand la carte bouge (le popup suit) et quand le
  // popup change de taille (photo chargée, page de carrousel).
  function setupControlsPopupOverlap() {
    const mapEl = carteMap.getContainer();
    const controlsEl = mapEl.querySelector('.leaflet-top.leaflet-right');
    if (!controlsEl) return;
    let openPopupEl = null;
    let popupResizeObserver = null;

    const update = () => {
      let overlaps = false;
      if (openPopupEl) {
        const a = openPopupEl.getBoundingClientRect();
        const b = controlsEl.getBoundingClientRect();
        overlaps = a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
      }
      mapEl.classList.toggle('carte-map--controls-hidden', overlaps);
    };

    carteMap.on('popupopen', (e) => {
      openPopupEl = e.popup.getElement();
      popupResizeObserver?.disconnect();
      if (typeof ResizeObserver !== 'undefined' && openPopupEl) {
        popupResizeObserver = new ResizeObserver(update);
        popupResizeObserver.observe(openPopupEl);
      }
      update();
    });
    carteMap.on('popupclose', () => {
      openPopupEl = null;
      popupResizeObserver?.disconnect();
      popupResizeObserver = null;
      update();
    });
    carteMap.on('move zoomend', update);
  }

  function addMarkerToMap(marker) {
    if (clusterLayer) {
      clusterLayer.addLayer(marker);
      return;
    }
    marker.addTo(carteMap);
  }

  function buildCarousel(items) {
    let pageIdx = 0;
    const total = items.length;

    const buildPageHtml = (index) =>
      items[index].popupHtml
      + '<div class="popup-nav-row">'
      + '<button class="popup-btn-prev">&#8249;</button>'
      + `<span class="popup-nav-count">${index + 1} / ${total}</span>`
      + '<button class="popup-btn-next">&#8250;</button>'
      + '</div>';

    const attachPageHandlers = (popup) => {
      const el = popup.getElement();
      el?.querySelector('.popup-btn-prev')?.addEventListener('click', (e) => {
        e.stopPropagation();
        pageIdx = (pageIdx - 1 + total) % total;
        popup.setContent(buildPageHtml(pageIdx));
        attachPageHandlers(popup);
      });
      el?.querySelector('.popup-btn-next')?.addEventListener('click', (e) => {
        e.stopPropagation();
        pageIdx = (pageIdx + 1) % total;
        popup.setContent(buildPageHtml(pageIdx));
        attachPageHandlers(popup);
      });
    };

    return { buildPageHtml, attachPageHandlers, getPageIdx: () => pageIdx, resetPageIdx: (index = 0) => { pageIdx = index; } };
  }

  function createGroupedPhotoMarkers({ getPhotos, apiPath, iconHtml, iconSize, markersArray, markerType }) {
    if (!carteMap) return;
    const photos = getPhotos ? getPhotos() : [];
    const autoPanOpts = getPopupAutoPanOpts();
    const popupOptions = { maxWidth: 510, minWidth: 0, ...autoPanOpts };

    const groups = new Map();
    for (const photo of photos) {
      if (photo.lat == null || photo.lng == null) continue;
      const key = `${snap(photo.lat)},${snap(photo.lng)}`;
      if (!groups.has(key)) groups.set(key, []);
      const pseudo = photo.pseudo ? `<div class="popup-pseudo">${escapeHtml(photo.pseudo)}</div>` : '';
      const imageUrl = `${apiPath}/${photo.id}/image?size=web`;
      const popupHtml = `<div class="popup-photo-wrap"><img src="${imageUrl}" alt="photo"></div>${pseudo}`;
      groups.get(key).push({ lat: photo.lat, lng: photo.lng, popupHtml, imageUrl, photoKey: `${markerType}-${photo.id}` });
    }

    const half = Math.round(iconSize[0] / 2);

    for (const items of groups.values()) {
      const lat = items.reduce((s, i) => s + i.lat, 0) / items.length;
      const lng = items.reduce((s, i) => s + i.lng, 0) / items.length;

      const mainIcon = L.divIcon({
        className: 'carte-marker-icon',
        html: iconHtml,
        iconSize,
        iconAnchor: [half, half],
        popupAnchor: [0, -(half + 2)],
      });

      const marker = L.marker([lat, lng], { icon: mainIcon, markerType, groupSize: items.length });
      addMarkerToMap(marker);
      markersArray.push(marker);
      items.forEach((item, index) => photoTargets.set(item.photoKey, { marker, index, imageUrl: item.imageUrl }));

      if (items.length === 1) {
        marker.bindPopup(items[0].popupHtml, popupOptions);
      } else {
        const carousel = buildCarousel(items);
        marker.bindPopup(carousel.buildPageHtml(0), popupOptions);
        marker.on('popupopen', (event) => {
          // Ouvert depuis Escales : directement sur la photo visée, sinon page 1.
          const start = pendingPopupStart?.marker === marker ? pendingPopupStart.index : 0;
          pendingPopupStart = null;
          carousel.resetPageIdx(start);
          event.popup.setContent(carousel.buildPageHtml(start));
          carousel.attachPageHandlers(event.popup);
        });
      }
    }
  }

  // Retourne une promesse résolue une fois la carte effectivement créée (ou
  // définitivement abandonnée). L'appelant (app.js) attend cette promesse
  // avant de déclencher le chargement des marqueurs : sans ça, si initMap()
  // doit retenter (cf. plus bas), updateCarteMarkers() s'exécute quand même
  // dans la foulée pendant que `carteMap` est encore undefined, échoue en
  // silence (ses propres gardes `if (!carteMap) return`), et ne se relance
  // jamais quand la carte finit par être prête — le loader restait alors
  // affiché indéfiniment.
  function initMap(retriesLeft = 60) {
    if (carteMap) {
      carteMap.invalidateSize();
      return Promise.resolve();
    }

    const el = document.getElementById('carte-map');
    if (!el) return Promise.resolve();
    if (el.offsetWidth === 0) {
      // Le conteneur n'a pas encore de largeur (tab tout juste révélé, mise
      // en page pas encore stabilisée) : on retente au frame suivant plutôt
      // que d'abandonner définitivement.
      if (retriesLeft <= 0) return Promise.resolve();
      return new Promise((resolve) => {
        requestAnimationFrame(() => resolve(initMap(retriesLeft - 1)));
      });
    }

    el.style.opacity = '0';
    carteMap = L.map('carte-map', { zoomControl: false }).setView([46.5, 2.3], 5);
    L.control.zoom({ position: 'topright' }).addTo(carteMap);
    L.tileLayer(CARTO_TILE_URL, CARTO_TILE_OPTIONS).addTo(carteMap);

    createClusterLayerIfAvailable();
    createMapLegendControl();
    createResetViewControl();
    createMapModeControl();
    setupControlsPopupOverlap();
    carteMap.on('zoomend moveend', () => {
      endProgrammaticViewChange();
      updateResetViewControlState();
    });
    return Promise.resolve();
  }

  async function updateMarkers() {
    if (!carteMap) return;

    const gen = ++markerGen;

    if (clusterLayer) {
      clusterLayer.clearLayers();
    } else {
      for (const marker of carteMarkers.values()) carteMap.removeLayer(marker);
      for (const marker of photoMarkers) carteMap.removeLayer(marker);
      for (const marker of mapPhotoMarkers) carteMap.removeLayer(marker);
    }
    carteMarkers.clear();
    photoMarkers.length = 0;
    mapPhotoMarkers.length = 0;
    photoTargets.clear();

    const groups = new Map();
    for (const item of getDashboardItems()) {
      if (item.lat == null || item.lng == null) continue;
      const key = `${snap(item.lat)},${snap(item.lng)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }

    if (groups.size === 0) return;

    const buf = await loadFont().catch(() => null);
    if (gen !== markerGen) return;

    const autoPanOpts = getPopupAutoPanOpts();
    for (const [key, items] of groups) {
      const lat = items.reduce((sum, c) => sum + c.lat, 0) / items.length;
      const lng = items.reduce((sum, c) => sum + c.lng, 0) / items.length;

      const itemsWithHtml = [];
      for (const item of items) {
        try {
          const svg = buf ? await resolveStaticSvg({
            script: item.script, text: item.text, knockoutSvg: item.knockoutSvg,
            generateSVG, fontBuffer: buf, color: '#ffffff',
          }) : '';
          const pseudo = item.pseudo ? `<div class="popup-pseudo">${escapeHtml(item.pseudo)}</div>` : '';
          itemsWithHtml.push({ ...item, popupHtml: `<div class="popup-svg-wrap">${svg}</div>${pseudo}` });
        } catch {
          const pseudo = item.pseudo ? `<div class="popup-pseudo">${escapeHtml(item.pseudo)}</div>` : '';
          itemsWithHtml.push({ ...item, popupHtml: `<strong>${escapeHtml(item.text)}</strong>${pseudo}` });
        }
        if (gen !== markerGen) return;
      }

      const mainIcon = L.divIcon({
        className: 'carte-marker-icon',
        html: '<svg class="carte-marker-dot" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="-1.5 -1.5 15 15"><circle cx="6" cy="6" r="6" fill="black" stroke="white" stroke-width="3" paint-order="stroke"/></svg>',
        iconSize: [14, 14],
        iconAnchor: [7, 7],
        popupAnchor: [0, -9],
      });

      const marker = L.marker([lat, lng], { icon: mainIcon, markerType: 'punchline', groupSize: itemsWithHtml.length });
      addMarkerToMap(marker);
      carteMarkers.set(key, marker);

      if (itemsWithHtml.length === 1) {
        marker.bindPopup(itemsWithHtml[0].popupHtml, { maxWidth: 430, minWidth: 350, ...autoPanOpts });
      } else {
        const carousel = buildCarousel(itemsWithHtml);
        marker.bindPopup(carousel.buildPageHtml(0), { maxWidth: 450, minWidth: 390, ...autoPanOpts });
        marker.on('popupopen', (event) => {
          carousel.resetPageIdx();
          event.popup.setContent(carousel.buildPageHtml(0));
          carousel.attachPageHandlers(event.popup);
        });
      }
    }
  }

  const SQUARE_SVG = '<svg class="carte-photo-dot" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="-1.5 -1.5 15 15"><rect x="0" y="0" width="12" height="12" rx="1" fill="black" stroke="white" stroke-width="3" paint-order="stroke"/></svg>';

  function updatePhotoMarkers() {
    createGroupedPhotoMarkers({
      getPhotos: getArPhotos,
      apiPath: '/api/ar-photos',
      iconHtml: SQUARE_SVG,
      iconSize: [14, 14],
      markersArray: photoMarkers,
      markerType: 'ar',
    });
  }

  const STAR_SVG = '<svg class="carte-map-photo-dot" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="-1 -1 16 16"><polygon points="7,0 8.54,4.9 13.72,4.9 9.52,7.98 11.06,12.74 7,9.8 2.94,12.74 4.48,7.98 0.28,4.9 5.46,4.9" fill="black" stroke="white" stroke-width="3" paint-order="stroke"/></svg>';

  function updateMapPhotoMarkers() {
    createGroupedPhotoMarkers({
      getPhotos: getMapPhotos,
      apiPath: '/api/map-photos',
      iconHtml: STAR_SVG,
      iconSize: [16, 16],
      markersArray: mapPhotoMarkers,
      markerType: 'map',
    });
  }

  function invalidateSize() {
    if (!carteMap) return;
    carteMap.invalidateSize();
  }

  function fitBoundsToMarkers() {
    if (!carteMap) return;
    const el = document.getElementById('carte-map');

    // initialView sert de référence pour "l'utilisateur est-il revenu à la vue
    // d'accueil ?" (bouton Retour/Tout voir) : on ne la (re)calcule qu'avant le
    // premier auto-fit, pour qu'elle reste stable et cohérente avec la position
    // réellement appliquée à la carte. La recalculer à chaque appel (ex. arrivée
    // asynchrone de nouveaux marqueurs) la ferait dériver sans jamais redéplacer
    // la carte (hasAutoFit bloque déjà tout second setView), rendant la détection
    // de "vue d'accueil" incohérente.
    if (!hasAutoFit) {
      const allLatLngs = [
        ...Array.from(carteMarkers.values()),
        ...photoMarkers,
        ...mapPhotoMarkers,
      ].map((m) => m.getLatLng());

      const nextInitialView = buildResetViewFromLatLngs(allLatLngs);
      if (nextInitialView) {
        const sw = nextInitialView.bounds.getSouthWest();
        const ne = nextInitialView.bounds.getNorthEast();
        initialView = {
          center: L.latLng(nextInitialView.center.lat, nextInitialView.center.lng),
          zoom: nextInitialView.zoom,
          bounds: L.latLngBounds([sw, ne]),
        };
      } else {
        initialView = null;
      }

      if (initialView) {
        beginProgrammaticViewChange();
        fitBoundsWithReserve(initialView.bounds, {
          ...getFitBoundsPadding(),
          maxZoom: RESET_VIEW_MAX_ZOOM,
        });
        // fitBounds décale le centre pour respecter le padding asymétrique :
        // on aligne initialView sur la position réellement appliquée
        initialView = { ...initialView, center: carteMap.getCenter(), zoom: carteMap.getZoom() };
        hasAutoFit = true;
      }
    }
    updateResetViewControlState();
    if (el) el.style.opacity = '1';
    document.getElementById('carte-map-loading')?.setAttribute('hidden', '');
    // Centrage sur photo (?photo=) : une seule fois, et seulement quand les
    // punchlines sont placées (le 1er passage arrive souvent avant elles, ce
    // qui fausserait le zoom visé, cf. getUnclusteredZoom).
    if (pendingPhotoKey && carteMarkers.size > 0) {
      const photoKey = pendingPhotoKey;
      pendingPhotoKey = null;
      focusPhoto(photoKey);
    }
  }

  // ── Lien "Voir sur la carte" de la page Escales (/carte?photo=ar-12), même
  // principe que sur /equalearth : centre la carte sur la photo au plus petit
  // zoom où son marqueur n'est plus regroupé (borné entre PHOTO_FOCUS_MIN_ZOOM
  // et PHOTO_FOCUS_MAX_ZOOM pour garder un peu de contexte géographique ; si
  // le marqueur est encore regroupé au zoom plafond, le groupe est déployé),
  // puis ouvre son popup directement sur cette photo. Une seule fois, après
  // le cadrage initial : un retour sur l'onglet Carte ne recentre pas la
  // carte. ──
  const PHOTO_FOCUS_MIN_ZOOM = 5;
  const PHOTO_FOCUS_MAX_ZOOM = 10;

  function getUnclusteredZoom(marker) {
    // Plus petit zoom où aucun autre marqueur n'est à moins de
    // CLUSTER_RADIUS px de celui-ci (même critère que markercluster, calculé
    // à partir des positions plutôt que de l'arbre interne de la bibliothèque).
    const others = [...carteMarkers.values(), ...photoMarkers, ...mapPhotoMarkers]
      .filter((m) => m !== marker)
      .map((m) => m.getLatLng());
    const target = marker.getLatLng();
    for (let zoom = PHOTO_FOCUS_MIN_ZOOM; zoom < PHOTO_FOCUS_MAX_ZOOM; zoom++) {
      const p = carteMap.project(target, zoom);
      if (others.every((latlng) => carteMap.project(latlng, zoom).distanceTo(p) > CLUSTER_RADIUS)) return zoom;
    }
    return PHOTO_FOCUS_MAX_ZOOM;
  }

  function focusPhoto(photoKey) {
    const target = photoTargets.get(photoKey);
    if (!target || !carteMap) return;
    const { marker, imageUrl } = target;
    const zoom = getUnclusteredZoom(marker);

    // Photo préchargée pendant l'animation : sans ça, Leaflet mesure le popup
    // AVANT l'arrivée de l'image (autoPan calculé trop court) et la photo
    // déborde ensuite sous le header -- cf. waitForPopupImages() sur
    // /equalearth. Timeout de secours (image cassée/lente).
    const imageReady = new Promise((resolve) => {
      const img = new Image();
      img.onload = img.onerror = resolve;
      img.src = imageUrl;
      setTimeout(resolve, 3000);
    });

    // Les marqueurs ont pu être reconstruits pendant l'animation (arrivée des
    // punchlines, temps réel) : on retrouve celui de la photo à chaque étape.
    const openTargetPopup = async () => {
      await imageReady;
      const current = photoTargets.get(photoKey);
      if (!current) return;
      pendingPopupStart = current;
      current.marker.openPopup();
    };

    // Arrivée sur un zoom : on attend la fin de l'animation de regroupement de
    // markercluster (spiderfy() est ignoré pendant celle-ci), puis, si le
    // marqueur est encore regroupé (estimation faite avant l'arrivée de tous
    // les marqueurs, ou un peu optimiste), on zoome encore ; au zoom plafond,
    // on déploie le groupe. Les moveend d'autres zooms (cadrage, redimensionnement) sont
    // ignorés, et on abandonne si l'animation n'aboutit pas (interrompue par
    // l'utilisateur).
    let giveUpTimer = null;
    let onMoveEnd = null;
    const stopWaiting = () => {
      clearTimeout(giveUpTimer);
      if (onMoveEnd) carteMap.off('moveend', onMoveEnd);
    };
    const goToZoom = (targetZoom, move) => {
      stopWaiting();
      onMoveEnd = () => {
        if (carteMap.getZoom() !== targetZoom) return;
        stopWaiting();
        setTimeout(() => settleAt(targetZoom), 350);
      };
      giveUpTimer = setTimeout(stopWaiting, 5000);
      beginProgrammaticViewChange();
      carteMap.on('moveend', onMoveEnd);
      move();
    };
    const settleAt = (currentZoom) => {
      const current = photoTargets.get(photoKey);
      if (!current) return;
      const parent = clusterLayer?.getVisibleParent(current.marker);
      if (!parent || parent === current.marker || typeof parent.spiderfy !== 'function') {
        openTargetPopup();
      } else if (currentZoom < PHOTO_FOCUS_MAX_ZOOM) {
        // Estimation refaite avec les marqueurs actuels (les punchlines ont pu
        // arriver après le départ de l'animation).
        const next = Math.max(currentZoom + 1, getUnclusteredZoom(current.marker));
        goToZoom(next, () => carteMap.setView(current.marker.getLatLng(), next, { animate: true }));
      } else {
        clusterLayer.once('spiderfied', () => setTimeout(openTargetPopup, 350));
        parent.spiderfy();
      }
    };

    goToZoom(zoom, () => carteMap.flyTo(marker.getLatLng(), zoom, { duration: 1.2 }));
  }

  return {
    initMap,
    updateMarkers,
    updatePhotoMarkers,
    updateMapPhotoMarkers,
    invalidateSize,
    fitBoundsToMarkers,
  };
}
